import React from "react";
import ReactSelect from "react-select";

import { Column } from "@tanstack/react-table";
import { DateRangeSelect } from "../../../atoms";
import { selectBaseClassNames } from "../../../atoms/PaziaSelect/styles";

export type TableFilterVariant =
  | "text"
  | "select"
  | "date-range"
  | "id"
  | "multi-select";
export function TableHeaderFilter({
  column,
}: {
  column: Column<any, unknown>;
}) {
  const { filterVariant, options } = (column.columnDef.meta ?? {}) as any;

  if (filterVariant === "id") {
    return (
      <DebouncedInput
        onChange={(value) => column.setFilterValue(value)}
        value={(column.getFilterValue() as string) ?? ""}
        className="max-w-20"
        placeholder="Id"
      />
    );
  }

  const targetHeight = 32;

  if (filterVariant === "date-range")
    return (
      <DateRangeSelect
        onChange={([from, to]) => {
          column.setFilterValue([from?.toISOString(), to?.toISOString()]);
        }}
        className="font-normal text-sm"
        fromName="from"
        toName="to"
        range={[new Date(2000, 1, 1), new Date(2090, 1, 1)]}
      />
    );
  if (filterVariant === "multi-select")
    return (
      <ReactSelect
        options={options}
        classNames={selectBaseClassNames()}
        isClearable={true}
        isMulti={true}
        defaultValue={
          Array.isArray(column.getFilterValue())
            ? options.filter((x: any) =>
                (column.getFilterValue() as any[]).includes(x.value)
              )
            : []
        }
        className="w-full font-normal text-sm"
        styles={{
          control: (provided) => ({
            ...provided,
            boxShadow: "none",
            minHeight: "initial",
          }),
          container: (provided) => ({
            ...provided,
            width: "100%",
          }),
          valueContainer: (base: any) => ({
            ...base,
            minHeight: `${targetHeight - 1 - 1}px`,
            padding: "0 8px",
          }),
          clearIndicator: (base: any) => ({
            ...base,
            padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
          }),
          dropdownIndicator: (base: any) => ({
            ...base,
            padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
          }),
        }}
        placeholder="Wybierz..."
        onChange={(selectedOptions: any) => {
          const values = selectedOptions
            ? selectedOptions.map((opt: any) => opt.value)
            : [];
          column.setFilterValue(values.length > 0 ? values : undefined);
        }}
      />
    );
  if (filterVariant === "select")
    return (
      <ReactSelect
        options={options}
        classNames={selectBaseClassNames()}
        isClearable={true}
        defaultValue={options.find(
          (x: any) => x.value == column.getFilterValue()
        )}
        className="w-full font-normal text-sm"
        styles={{
          control: (provided) => ({
            ...provided,
            boxShadow: "none",
            minHeight: "initial",
          }),
          container: (provided) => ({
            ...provided,
            width: "100%",
          }),
          valueContainer: (base: any) => ({
            ...base,
            height: `${targetHeight - 1 - 1}px`,
            padding: "0 8px",
          }),
          clearIndicator: (base: any) => ({
            ...base,
            padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
          }),
          dropdownIndicator: (base: any) => ({
            ...base,
            padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
          }),
        }}
        placeholder="Wybierz..."
        onChange={(option: any) => {
          column.setFilterValue(option?.value?.toString() ?? "");
        }}
      />
    );
  return (
    <DebouncedInput
      onChange={(value) => column.setFilterValue(value)}
      value={(column.getFilterValue() as string) ?? ""}
      name={column.id}
    />
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue = "",
  onChange,
  debounce = 500,
  ...props
}: {
  onChange: (value: string | number | undefined) => void;
  value?: string | number;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      className={` form-input h-[2rem] w-full font-normal text-sm dark:bg-slate-800 ${props.className ?? ""}`}
      placeholder={props.placeholder ?? "Szukaj..."}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}
